import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  DirectionsBus,
  CalendarToday,
  Warning,
  AcUnit,
  Whatshot,
  ViewModule,
  ViewDay,
  People,
  BugReport,
} from '@mui/icons-material';
import { collection, query, getDocs, doc, getDoc, orderBy, startAt, endAt, where, onSnapshot } from 'firebase/firestore';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { Operation, MonthlyOperationStats } from '../types/operation';
import { Route } from '../types/route';
import { RouteMonthlyCheckin } from '../types/boarding';
import { useAuth } from '../contexts/AuthContext';

type ViewMode = 'monthly' | 'daily';
const LEGACY_APP_VERSION_LABEL = '1.0.8 이전';

export const OperationPage: React.FC = () => {
  const navigate = useNavigate();
  const { adminType } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('daily');
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [operations, setOperations] = useState<Operation[]>([]);
  const [routeNames, setRouteNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showTestData, setShowTestData] = useState(false); // 테스트 데이터 보기 (기본값: false)
  const [latestAppVersion, setLatestAppVersion] = useState<string>('');

  const parseVersionSegments = (version: string): number[] => {
    const versionCore = version.trim().replace(/^v/i, '').split(/[+-]/)[0];
    return versionCore.split('.').map((segment) => {
      const value = parseInt(segment.replace(/\D/g, ''), 10);
      return Number.isNaN(value) ? 0 : value;
    });
  };

  const compareVersions = (a: string, b: string): number => {
    const aSegments = parseVersionSegments(a);
    const bSegments = parseVersionSegments(b);
    const length = Math.max(aSegments.length, bSegments.length);

    for (let index = 0; index < length; index += 1) {
      const aValue = aSegments[index] || 0;
      const bValue = bSegments[index] || 0;

      if (aValue !== bValue) {
        return aValue - bValue;
      }
    }

    return 0;
  };

  const getOperationAppVersionLabel = (version?: string): string => {
    const normalizedVersion = version?.trim();
    return normalizedVersion && normalizedVersion !== '-' ? normalizedVersion : LEGACY_APP_VERSION_LABEL;
  };

  const isOutdatedAppVersion = (version?: string): boolean => {
    const normalizedVersion = version?.trim();

    if (!normalizedVersion || normalizedVersion === '-') {
      return true;
    }

    if (!latestAppVersion) {
      return false;
    }

    return compareVersions(normalizedVersion, latestAppVersion) < 0;
  };

  // 월 선택 옵션 생성 (최근 12개월)
  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = format(date, 'yyyyMM');
      const label = format(date, 'yyyy년 MM월', { locale: ko });
      options.push({ value: yearMonth, label });
    }

    return options;
  }, []);

  // 초기 선택: 현재 월과 오늘 날짜
  useEffect(() => {
    if (monthOptions.length > 0 && !selectedYearMonth) {
      setSelectedYearMonth(monthOptions[0].value);
    }
    if (!selectedDate) {
      setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
    }
  }, [monthOptions, selectedYearMonth, selectedDate]);

  useEffect(() => {
    const fetchLatestAppVersion = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'app_status'));
        const statusDoc = snapshot.docs.find((doc) => typeof doc.data().lastVersion === 'string');
        setLatestAppVersion(statusDoc?.data().lastVersion || '');
      } catch (error) {
        console.error('Error fetching latest app version:', error);
      }
    };

    fetchLatestAppVersion();
  }, []);

  // 관리자 타입에 따른 노선 필터
  const routeFilter = useMemo(() => {
    switch (adminType) {
      case 'route1':
        return '강서구청';
      case 'route2':
        return '하단';
      default:
        return null; // super admin - no filter
    }
  }, [adminType]);

  // 승차 인원 데이터 조회 및 운행 기록에 통합
  const enrichOperationsWithCheckinData = async (operations: Operation[], yearMonth: string) => {
    try {
      // route_monthly_checkins에서 해당 월의 모든 데이터 조회
      const checkinQuery = query(
        collection(db, 'route_monthly_checkins'),
        where('year_month', '==', yearMonth)
      );

      const checkinSnapshot = await getDocs(checkinQuery);
      const checkinDataMap: Record<string, RouteMonthlyCheckin> = {};

      checkinSnapshot.docs.forEach((doc) => {
        const data = doc.data() as RouteMonthlyCheckin;
        // Document ID 형식: routeId_YYYYMM
        const routeId = doc.id.split('_')[0];
        checkinDataMap[routeId] = { ...data, id: doc.id };
      });

      // 각 운행 기록에 대해 해당 날짜의 승차 인원 계산
      operations.forEach((operation) => {
        const checkinData = checkinDataMap[operation.routeId];

        if (checkinData && checkinData.checkins) {
          // 해당 날짜의 승차 기록만 필터링
          const dateCheckins = checkinData.checkins.filter((checkin) => {
            if (checkin.timestamp && checkin.timestamp.toDate) {
              const checkinDate = format(checkin.timestamp.toDate(), 'yyyy-MM-dd');
              return checkinDate === operation.operationDate;
            }
            return false;
          });

          operation.totalPassengers = dateCheckins.length;
        } else {
          operation.totalPassengers = 0;
        }
      });
    } catch (error) {
      console.error('Error fetching checkin data:', error);
      // 에러 발생 시 승차 인원을 0으로 설정
      operations.forEach((operation) => {
        operation.totalPassengers = 0;
      });
    }
  };

  // 운행 기록 실시간 구독 (월별 또는 일별)
  useEffect(() => {
    // 월별 모드에서는 selectedYearMonth, 일별 모드에서는 selectedDate 확인
    if (viewMode === 'monthly' && !selectedYearMonth) return;
    if (viewMode === 'daily' && !selectedDate) return;

    setLoading(true);

    let startDate: string;
    let endDate: string;

    if (viewMode === 'monthly') {
      // 선택한 월의 시작일과 종료일 계산
      const year = parseInt(selectedYearMonth.substring(0, 4));
      const month = parseInt(selectedYearMonth.substring(4, 6)) - 1;
      startDate = format(startOfMonth(new Date(year, month)), 'yyyy-MM-dd');
      endDate = format(endOfMonth(new Date(year, month)), 'yyyy-MM-dd');
    } else {
      // 일별 모드: 선택한 날짜만 조회
      startDate = selectedDate;
      endDate = selectedDate;
    }

    console.log('Subscribing to operations:', { viewMode, startDate, endDate });

    // Document ID 범위 쿼리를 사용하여 데이터 구독
    const q = query(
      collection(db, 'operations'),
      orderBy('__name__'), // Document ID로 정렬
      startAt(startDate), // 시작 날짜
      endAt(endDate + '\uf8ff') // 종료 날짜 + 유니코드 최대값
    );

    // 실시간 구독
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        console.log('Operations updated:', snapshot.size);

        const data = snapshot.docs
          .map((doc) => {
            const docData = doc.data();
            // Document ID에서 날짜 추출 (형식: YYYY-MM-DD_routeId)
            const operationDate = doc.id.split('_')[0];

            return {
              id: doc.id,
              operationDate,
              routeId: docData.routeId || '',
              routeName: docData.routeName || '',
              vehicleNumber: docData.vehicleNumber || '',
              driverName: docData.driverName || '',
              usedVersion: docData.usedVersion || '',
              status: docData.status || '',
              startTime: docData.startTime,
              endTime: docData.endTime,
              currentStopIndex: docData.currentStopIndex || 0,
              totalStops: docData.totalStops || 0,
              isDelayed: docData.isDelayed || false,
              delayMinutes: docData.delayMinutes || 0,
              totalPassengers: docData.totalPassengers || 0,
              // 민원 건수
              isHot: docData.isHot || 0,
              isCold: docData.isCold || 0,
              isDanger: docData.isDanger || 0,
              // 테스트 모드
              isTestMode: docData.isTestMode || false,
            } as Operation;
          })
          .filter((operation) => {
            // 테스트 데이터 필터링
            if (showTestData) {
              // 테스트 데이터만 보기: isTestMode === true인 것만
              return operation.isTestMode === true;
            } else {
              // 일반 데이터만 보기: isTestMode가 false이거나 없는 것만
              return !operation.isTestMode;
            }
          });

        // 승차 인원 데이터 조회 및 통합
        await enrichOperationsWithCheckinData(data, viewMode === 'monthly' ? selectedYearMonth : format(new Date(selectedDate), 'yyyyMM'));

        // 노선명이 없는 경우에만 routes 컬렉션에서 조회
        const routeNameMap: Record<string, string> = {};
        const uniqueRouteIds = new Set<string>();

        data.forEach((operation) => {
          if (!operation.routeName) {
            uniqueRouteIds.add(operation.routeId);
          } else {
            routeNameMap[operation.routeId] = operation.routeName;
          }
        });

        // 노선명이 없는 노선만 routes 컬렉션에서 조회
        const routeIdsArray = Array.from(uniqueRouteIds);
        for (const routeId of routeIdsArray) {
          try {
            const routeDoc = await getDoc(doc(db, 'routes', routeId));
            if (routeDoc.exists()) {
              const routeData = routeDoc.data() as Route;
              routeNameMap[routeId] = routeData.name;
            } else {
              routeNameMap[routeId] = routeId; // 노선을 찾을 수 없으면 ID 사용
            }
          } catch (error) {
            console.error(`Error fetching route ${routeId}:`, error);
            routeNameMap[routeId] = routeId; // 에러 발생 시 ID 사용
          }
        }

        // 관리자 타입에 따라 운행 기록 필터링
        const filteredData = routeFilter
          ? data.filter((operation) => {
              const routeName = operation.routeName || routeNameMap[operation.routeId] || operation.routeId;
              return routeName.includes(routeFilter);
            })
          : data; // super admin은 필터링 없이 모든 데이터 표시

        setOperations(filteredData);
        setRouteNames(routeNameMap);
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to operations:', error);
        setLoading(false);
      }
    );

    // 구독 해제 함수 반환
    return () => {
      console.log('Unsubscribing from operations');
      unsubscribe();
    };
  }, [viewMode, selectedYearMonth, selectedDate, routeFilter, showTestData]);

  // 정렬된 운행 기록 (날짜 역순 -> 노선명 오름차순)
  const sortedOperations = useMemo(() => {
    return [...operations].sort((a, b) => {
      // 1순위: 날짜 역순 정렬 (최신순)
      const dateCompare = b.operationDate.localeCompare(a.operationDate);
      if (dateCompare !== 0) return dateCompare;

      // 2순위: 같은 날짜 내에서 노선명 오름차순 정렬
      const routeNameA = routeNames[a.routeId] || a.routeName || a.routeId;
      const routeNameB = routeNames[b.routeId] || b.routeName || b.routeId;
      return routeNameA.localeCompare(routeNameB, 'ko');
    });
  }, [operations, routeNames]);

  // 월별 통계 계산
  const statistics = useMemo<MonthlyOperationStats>(() => {
    const uniqueRoutes = new Set(operations.map((op) => op.routeId));
    const completedOps = operations.filter((op) => op.status === 'COMPLETED' || op.status === 'COMPLETE');
    const delayedOps = operations.filter((op) => op.isDelayed);

    // 민원 건수 집계
    const hotComplaints = operations.reduce((sum, op) => sum + (op.isHot || 0), 0);
    const coldComplaints = operations.reduce((sum, op) => sum + (op.isCold || 0), 0);
    const dangerComplaints = operations.reduce((sum, op) => sum + (op.isDanger || 0), 0);

    return {
      totalOperations: operations.length,
      completedOperations: completedOps.length,
      delayedOperations: delayedOps.length,
      routeCount: uniqueRoutes.size,
      totalComplaints: hotComplaints + coldComplaints + dangerComplaints,
      hotComplaints,
      coldComplaints,
      dangerComplaints,
    };
  }, [operations]);

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                운행 기록
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'monthly' ? '월별' : '일별'} 운행 기록 및 불편 사항 통계를 확인합니다.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* 테스트 데이터 보기 (최고 관리자만) */}
            {adminType === 'super' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showTestData}
                    onChange={(e) => setShowTestData(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BugReport sx={{ fontSize: 18, color: showTestData ? '#f59e0b' : '#94a3b8' }} />
                    <Typography variant="body2" color={showTestData ? '#f59e0b' : 'text.secondary'}>
                      테스트 데이터
                    </Typography>
                  </Box>
                }
              />
            )}

            {/* 월별/일별 전환 */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
            >
              <ToggleButton value="monthly">
                <ViewModule sx={{ mr: 1 }} />
                월별
              </ToggleButton>
              <ToggleButton value="daily">
                <ViewDay sx={{ mr: 1 }} />
                일별
              </ToggleButton>
            </ToggleButtonGroup>

            {/* 월 선택 또는 날짜 선택 */}
            {viewMode === 'monthly' ? (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>조회 월</InputLabel>
                <Select
                  value={selectedYearMonth}
                  label="조회 월"
                  onChange={(e) => setSelectedYearMonth(e.target.value)}
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                type="date"
                label="조회 날짜"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ minWidth: 200 }}
              />
            )}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 통계 카드 */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <DirectionsBus sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    총 운행 횟수
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {statistics.totalOperations.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {statistics.routeCount}개 노선
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CalendarToday sx={{ fontSize: 32, color: '#10b981' }} />
                  <Typography variant="h6" fontWeight={600}>
                    완료된 운행
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {statistics.completedOperations.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {statistics.totalOperations > 0
                    ? ((statistics.completedOperations / statistics.totalOperations) * 100).toFixed(1)
                    : 0}
                  % 완료
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Warning sx={{ fontSize: 32, color: '#f59e0b' }} />
                  <Typography variant="h6" fontWeight={600}>
                    지연된 운행
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  {statistics.delayedOperations.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {statistics.totalOperations > 0
                    ? ((statistics.delayedOperations / statistics.totalOperations) * 100).toFixed(1)
                    : 0}
                  % 지연
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    민원 건수
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Whatshot sx={{ color: '#ef4444', fontSize: 18 }} />
                    <Typography variant="body2">더움:</Typography>
                    <Typography variant="body1" fontWeight={600} color="#ef4444">
                      {statistics.hotComplaints}건
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AcUnit sx={{ color: '#3b82f6', fontSize: 18 }} />
                    <Typography variant="body2">추움:</Typography>
                    <Typography variant="body1" fontWeight={600} color="#3b82f6">
                      {statistics.coldComplaints}건
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning sx={{ color: '#f59e0b', fontSize: 18 }} />
                    <Typography variant="body2">안전:</Typography>
                    <Typography variant="body1" fontWeight={600} color="#f59e0b">
                      {statistics.dangerComplaints}건
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* 운행 기록 테이블 */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  운행 기록 목록
                </Typography>
                {latestAppVersion && (
                  <Chip
                    label={`최신 기사앱 ${latestAppVersion}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>

              {operations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">
                    {viewMode === 'monthly' ? '선택한 월의' : '선택한 날짜의'} 운행 기록이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>운행 날짜</TableCell>
                        <TableCell>노선명</TableCell>
                        <TableCell>차량 번호</TableCell>
                        <TableCell>운전자</TableCell>
                        <TableCell align="center">기사앱 버전</TableCell>
                        <TableCell align="center">승차 인원</TableCell>
                        <TableCell align="center">운행 상태</TableCell>
                        <TableCell align="center">정류장</TableCell>
                        <TableCell align="center">지연</TableCell>
                        <TableCell align="center">더움</TableCell>
                        <TableCell align="center">추움</TableCell>
                        <TableCell align="center">안전운행</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedOperations.map((operation) => {
                        const routeName = routeNames[operation.routeId] || operation.routeName || operation.routeId;
                        const isOutdatedVersion = isOutdatedAppVersion(operation.usedVersion);

                        return (
                          <TableRow
                            key={operation.id}
                            hover
                            onClick={() => navigate(`/operation/${operation.id}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {format(new Date(operation.operationDate), 'yyyy-MM-dd (E)', {
                                  locale: ko,
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DirectionsBus sx={{ fontSize: 20, color: '#667eea' }} />
                                <Typography variant="body2">{routeName}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {operation.vehicleNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {operation.driverName || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color={isOutdatedVersion ? 'error' : 'text.primary'}
                                >
                                  {getOperationAppVersionLabel(operation.usedVersion)}
                                </Typography>
                                {isOutdatedVersion && (
                                  <Chip
                                    label="업데이트 필요"
                                    color="error"
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.75rem',
                                      fontWeight: 700,
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <People sx={{ fontSize: 18, color: '#667eea' }} />
                                <Typography variant="body2" fontWeight={600}>
                                  {operation.totalPassengers || 0}명
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={
                                  operation.status === 'COMPLETED' || operation.status === 'COMPLETE'
                                    ? '운행 종료'
                                    : operation.status === 'IN_PROGRESS'
                                    ? '운행 중'
                                    : operation.status
                                }
                                color={operation.status === 'COMPLETED' || operation.status === 'COMPLETE' ? 'success' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2">
                                {operation.currentStopIndex || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {operation.isDelayed ? (
                                <Chip
                                  icon={<Warning />}
                                  label={`${operation.delayMinutes || 0}분`}
                                  color="warning"
                                  size="small"
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {operation.isHot > 0 ? (
                                <Chip
                                  icon={<Whatshot />}
                                  label={operation.isHot}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#fee2e2',
                                    color: '#ef4444',
                                    '& .MuiChip-icon': { color: '#ef4444' },
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {operation.isCold > 0 ? (
                                <Chip
                                  icon={<AcUnit />}
                                  label={operation.isCold}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#dbeafe',
                                    color: '#3b82f6',
                                    '& .MuiChip-icon': { color: '#3b82f6' },
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {operation.isDanger > 0 ? (
                                <Chip
                                  icon={<Warning />}
                                  label={operation.isDanger}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#fef3c7',
                                    color: '#f59e0b',
                                    '& .MuiChip-icon': { color: '#f59e0b' },
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </MainLayout>
  );
};
